import { useCallback, useEffect } from "react";
import {
  useAudioContext,
  type AudioMediaDataModel,
} from "../../../domains/app/contexts/audio.context";
import { convertArticleToMediaData } from "../../../domains/audio/utils/convertArticleToMediaData";
import { memoryStore, useStore } from "../../../lib/store";
import { gaEventArticleAudioComplete } from "../../../tools/analytics/domains/article.analytics";
import { AnalyticsItemList } from "../../../tools/analytics/analytics.types";
import type { Api } from "../../../services/api";
import type { ArticleDetail } from "./useArticleData";
import { cancelSpeechRequest, requestArticleSpeech } from "./useArticleAudio";
import { isSpeechUrlIntact } from "../lib/streamSpeech";

type ArticleListItem =
  Api["get"]["/webapp/public/articles"]["output"]["body"]["docs"][number];

export type ListenableArticle = ArticleDetail | ArticleListItem;

type Queue = readonly ListenableArticle[];

/**
 * Where the desktop listening session is. The audio context plays one track;
 * this adds what it does not know: the queue the track came from (the feed or
 * collection the article was opened from), the synthesis before the first
 * byte, its failure, and the end of a track. `index` is the article the state
 * is about: the one being prepared, played or finished, or the next one.
 */
type Listening =
  | { status: "idle" }
  // `displaces` is the track the player held when the request began. Another
  // source loading a track meanwhile (a podcast) cancels the request, so a late
  // synthesis never replaces what the reader chose since.
  | {
      status: "preparing";
      queue: Queue;
      index: number;
      displaces: string | null;
    }
  | { status: "failed"; queue: Queue; index: number; displaces: string | null }
  | { status: "loaded"; queue: Queue; index: number }
  | { status: "ended"; queue: Queue; index: number }
  // `endsAt` (epoch ms) is when the next article starts on its own. It lives
  // here, not in the countdown on screen, so remounting the player (the
  // sidebar folding or unfolding) does not restart the count.
  | { status: "up-next"; queue: Queue; index: number; endsAt: number };

/** How long the Up next card waits before it plays the next article. */
export const UP_NEXT_MS = 5000;

const listeningStore = memoryStore<Listening>({ status: "idle" });

// The queue and index the loaded track was started with. Its end callback
// compares against these very values, so a track resumed after another
// article's request is put back under them, not under that request's queue.
let loadedTrackSession: { queue: Queue; index: number } | null = null;

function queueArticle(queue: Queue, index: number): ListenableArticle {
  const article = queue[index];
  if (article === undefined) {
    throw new Error(`should not happen: no article at queue index ${index}`);
  }
  return article;
}

/** What the sidebar player shows. */
export type SidebarListening =
  | { kind: "none" }
  | { kind: "preparing"; article: ListenableArticle }
  | { kind: "failed"; article: ListenableArticle }
  | {
      kind: "up-next";
      article: ListenableArticle;
      position: number;
      total: number;
      endsAt: number;
    }
  | {
      kind: "track";
      media: AudioMediaDataModel;
      /** False for a track outside an article queue (a podcast). */
      hasNext: boolean;
      /** Play or pause, through the session: replaying a finished article
       *  makes it the loaded one again. */
      onPlayPause: () => void;
      onSeek: (time: number) => void;
    };

/** What an article's Listen button shows. */
export type ArticleListening =
  | { state: "idle" }
  | { state: "preparing" }
  // Null while the track has no known length (a live stream).
  | { state: "playing"; remaining: number | null }
  | { state: "paused"; remaining: number | null }
  | { state: "ended" };

/**
 * The desktop listening session: the sidebar player, the article's Listen
 * button, and the chaining to the next article of the queue. Playback itself
 * stays in the audio context, shared with the phone.
 */
export function useDesktopArticleListening(): {
  sidebar: SidebarListening;
  articleListening: (articleId: string) => ArticleListening;
  /** Play or pause `article`, queued within `list` when it belongs to it. */
  listen: (article: ArticleDetail, list: ArticleListItem[]) => void;
  playNext: () => void;
  cancelUpNext: () => void;
  retry: () => void;
  close: () => void;
} {
  const {
    currentMediaContent,
    hasTrack,
    isPlaying,
    currentTime,
    duration,
    setTracks,
    pause,
    play,
    seek,
    togglePlayPause,
    clear,
  } = useAudioContext();
  const [listening] = useStore(listeningStore);

  // Article narration only: a room podcast has an article as its source too,
  // and pausing it is not what the article's Listen button means.
  const loadedArticleId =
    hasTrack &&
    currentMediaContent !== null &&
    currentMediaContent.type === "article_audio"
      ? currentMediaContent.source.id
      : null;
  const loadedTrackKey =
    hasTrack && currentMediaContent !== null
      ? `${currentMediaContent.type}:${currentMediaContent.source.id}`
      : null;

  useEffect(() => {
    const current = listeningStore.get();
    if (current.status !== "preparing" && current.status !== "failed") return;
    if (current.displaces === loadedTrackKey) return;
    listeningStore.set({ status: "idle" });
  }, [loadedTrackKey]);

  const start = useCallback(
    async (queue: Queue, index: number): Promise<void> => {
      const article = queueArticle(queue, index);
      const displaces = loadedTrackKey;
      listeningStore.set({ status: "preparing", queue, index, displaces });
      // The previous track stops while the next one is prepared: two voices
      // at once, or a stale one resuming, would be worse than a pause.
      pause();
      const isStillAsked = (status: Listening["status"]): boolean => {
        const current = listeningStore.get();
        if (current.status === "idle") return false;
        return (
          current.status === status &&
          current.queue === queue &&
          current.index === index
        );
      };

      const request = await requestArticleSpeech(article);
      if (!isStillAsked("preparing")) return;
      switch (request.status) {
        case "ready":
          break;
        case "superseded":
          return;
        case "failed":
          listeningStore.set({ status: "failed", queue, index, displaces });
          return;
        default:
          throw new Error(request satisfies never);
      }
      const track = request.track;

      setTracks({
        track,
        contentList: [convertArticleToMediaData(article, track.url)],
        index: 0,
        autoplay: true,
        onEndReached: () => {
          gaEventArticleAudioComplete({
            article,
            listName: AnalyticsItemList.ARTICLE_PAGE,
          });
          if (!isStillAsked("loaded")) return;
          listeningStore.set(
            index + 1 < queue.length
              ? {
                  status: "up-next",
                  queue,
                  index: index + 1,
                  endsAt: Date.now() + UP_NEXT_MS,
                }
              : { status: "ended", queue, index }
          );
        },
      });
      loadedTrackSession = { queue, index };
      listeningStore.set({ status: "loaded", queue, index });
    },
    [pause, setTracks, loadedTrackKey]
  );

  // The article that just finished, when the session is at the end of one.
  const endedIndex =
    listening.status === "ended"
      ? listening.index
      : listening.status === "up-next"
        ? listening.index - 1
        : null;
  const endedArticleId =
    endedIndex === null || listening.status === "idle"
      ? null
      : queueArticle(listening.queue, endedIndex).id;
  // A session state outlives its track when something else (a podcast, a
  // sign-out) replaced or cleared it: it then no longer describes the player.
  const isAtEndOfLoaded =
    endedArticleId !== null && endedArticleId === loadedArticleId;

  // Leaving the end of a finished article (replaying it, or seeking back
  // into it) makes it the loaded article of the queue again.
  const leaveEnd = (): void => {
    if (!isAtEndOfLoaded) return;
    switch (listening.status) {
      case "ended":
        listeningStore.set({
          status: "loaded",
          queue: listening.queue,
          index: listening.index,
        });
        return;
      case "up-next":
        listeningStore.set({
          status: "loaded",
          queue: listening.queue,
          index: listening.index - 1,
        });
        return;
      case "idle":
      case "preparing":
      case "failed":
      case "loaded":
        return;
      default:
        throw new Error(listening satisfies never);
    }
  };

  const sidebar = ((): SidebarListening => {
    switch (listening.status) {
      case "preparing":
      case "failed":
        return {
          kind: listening.status,
          article: queueArticle(listening.queue, listening.index),
        };
      case "up-next":
        if (isAtEndOfLoaded) {
          return {
            kind: "up-next",
            article: queueArticle(listening.queue, listening.index),
            position: listening.index + 1,
            total: listening.queue.length,
            endsAt: listening.endsAt,
          };
        }
        break;
      case "idle":
      case "loaded":
      case "ended":
        break;
      default:
        throw new Error(listening satisfies never);
    }
    if (!hasTrack || currentMediaContent === null) return { kind: "none" };
    // After a Cancel on Up next, the finished article still has a next one.
    const hasNext =
      (listening.status === "loaded" || listening.status === "ended") &&
      queueArticle(listening.queue, listening.index).id === loadedArticleId &&
      listening.index + 1 < listening.queue.length;
    return {
      kind: "track",
      media: currentMediaContent,
      hasNext,
      onPlayPause: () => {
        leaveEnd();
        togglePlayPause();
      },
      onSeek: (time) => {
        leaveEnd();
        seek(time);
      },
    };
  })();

  // Where the loaded track stands, for the one left paused while another
  // article is prepared or failed: finished (Up next moved on) or midway (the
  // reader started another article).
  const isLoadedTrackOver = duration > 0 && currentTime >= duration - 0.5;
  const remaining = duration > 0 ? Math.max(0, duration - currentTime) : null;
  // Its streamed audio can be cut off by the other request's stream: then
  // there is nothing left to resume, and it can only start over.
  const isLoadedTrackIntact =
    currentMediaContent !== null &&
    isSpeechUrlIntact(currentMediaContent.trackUrl);

  const articleListening = (articleId: string): ArticleListening => {
    switch (listening.status) {
      case "preparing":
      case "failed":
        if (
          listening.status === "preparing" &&
          queueArticle(listening.queue, listening.index).id === articleId
        ) {
          return { state: "preparing" };
        }
        if (loadedArticleId !== articleId || !isLoadedTrackIntact) {
          return { state: "idle" };
        }
        return isLoadedTrackOver
          ? { state: "ended" }
          : { state: "paused", remaining };
      case "idle":
      case "loaded":
      case "ended":
      case "up-next":
        break;
      default:
        throw new Error(listening satisfies never);
    }
    if (loadedArticleId !== articleId) return { state: "idle" };
    if (isAtEndOfLoaded) return { state: "ended" };
    return isPlaying
      ? { state: "playing", remaining }
      : { state: "paused", remaining };
  };

  const listen = (article: ArticleDetail, list: ArticleListItem[]): void => {
    // The track left paused while another article is prepared or failed:
    // Listen drops that request and goes back to this one, where it stood.
    if (
      loadedArticleId === article.id &&
      (listening.status === "preparing" || listening.status === "failed") &&
      queueArticle(listening.queue, listening.index).id !== article.id
    ) {
      // That request would otherwise open a stream, which cuts off and
      // revokes this article's own streamed audio.
      cancelSpeechRequest(queueArticle(listening.queue, listening.index).id);
      const session = loadedTrackSession;
      const isOwnSession =
        session !== null &&
        queueArticle(session.queue, session.index).id === article.id;
      // Its streamed audio was already cut off: start it over.
      if (!isLoadedTrackIntact) {
        void (isOwnSession
          ? start(session.queue, session.index)
          : start([article], 0));
        return;
      }
      // Back under the queue it was started with, so its Up next still follows.
      listeningStore.set(
        isOwnSession
          ? { status: "loaded", queue: session.queue, index: session.index }
          : { status: "idle" }
      );
      if (isLoadedTrackOver) seek(0);
      play();
      return;
    }
    if (
      loadedArticleId === article.id &&
      listening.status !== "preparing" &&
      listening.status !== "failed"
    ) {
      if (isAtEndOfLoaded) {
        leaveEnd();
        seek(0);
        play();
        return;
      }
      togglePlayPause();
      return;
    }
    // The page's own copy of the article replaces its entry in the list: it
    // carries the detail fields, and the audio the page may have just learnt.
    const index = list.findIndex((item) => item.id === article.id);
    void (index === -1
      ? start([article], 0)
      : start(
          list.map((item) => (item.id === article.id ? article : item)),
          index
        ));
  };

  const playNext = (): void => {
    switch (listening.status) {
      case "up-next":
        void start(listening.queue, listening.index);
        return;
      case "loaded":
      case "ended":
        void start(listening.queue, listening.index + 1);
        return;
      case "idle":
      case "preparing":
      case "failed":
        throw new Error(
          `should not happen: no next article while ${listening.status}`
        );
      default:
        throw new Error(listening satisfies never);
    }
  };

  const cancelUpNext = (): void => {
    if (listening.status !== "up-next") {
      throw new Error(`should not happen: cancel while ${listening.status}`);
    }
    listeningStore.set({
      status: "ended",
      queue: listening.queue,
      index: listening.index - 1,
    });
  };

  const retry = (): void => {
    if (listening.status !== "failed") {
      throw new Error(`should not happen: retry while ${listening.status}`);
    }
    void start(listening.queue, listening.index);
  };

  const close = (): void => {
    loadedTrackSession = null;
    clear();
    listeningStore.set({ status: "idle" });
  };

  return {
    sidebar,
    articleListening,
    listen,
    playNext,
    cancelUpNext,
    retry,
    close,
  };
}
